import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { Autosave } from "../lib/autosave";
import { DocumentDraft } from "../lib/document_draft";
import { blankSlide, isBlankSlide, legacyHtmlOf } from "../lib/composition_document";

/**
 * L'orchestrateur de l'éditeur de compositions images — le pendant de composition-editor
 * (vidéo) : il POSSÈDE le source_data et la page courante, porte toutes les mutations du
 * document (sélection, duplication, suppression, réordonnancement, sauvegarde) et configure
 * ses surfaces par outlets :
 *  - fabric : le canvas, qui n'édite que les layers de la page courante (absent dans l'îlot
 *    strip du publisher — les guards font le tri) ;
 *  - composition-editor--slides : la bande, vue muette qui lui remonte les intentions.
 *
 * La bande est rendue SERVEUR dans les deux modes (îlot compositions#show+strip, éditeur
 * compositions#show+editor_strip) et morphée par le broadcast-signal du Bearer à chaque save — seule la
 * sélection (ring, barre repeat) reste un état client, rejoué après chaque rendu du frame.
 *
 * Deux stratégies de persistance : l'autosave de l'éditeur sur son brouillon (document-url ;
 * le commit et Quitter : lib/document_draft), ou le mode persisté de l'îlot (update-url
 * présente → chaque mutation PATCHe la compo).
 * Dans l'îlot, cliquer une slide ouvre l'éditeur dans le panel, centré sur elle : le formulaire
 * du brouillon (draftForm) est soumis, le serveur fait le reste (lib/frame_hosts).
 *
 * Naming: "image-composition-editor" in HTML.
 */
export default class extends Controller {
  static outlets = ["fabric", "composition-editor--slides"];

  static targets = ["stripFrame", "htmlPreview", "saveState",
                    "repeatToggle", "repeatFields", "repeatMin", "repeatMax", "importField",
                    // Îlot : le formulaire qui crée le brouillon sur lequel le panel s'ouvre
                    // (POST draft, cible le frame document_editor) et son champ `slide` ; celui
                    // qui retire le visuel (DELETE document, la compo redevient vierge).
                    "draftForm", "slideField", "clearForm"];

  static values = {
    sourceData: Object,
    updateUrl: String,
    documentUrl: String,
    // Garder une sélection en calque (stickers#create) : l'endpoint et le porteur, quand
    // l'éditeur l'offre (un visuel avec autosave).
    stickerUrl: String,
    initialCurrent: Number,
    // La page vierge d'un document né sans page : servie par la vue, pas encore sauvée
    unsavedPage: Boolean,
    // Le contrat des brouillons (plan document-drafts, lib/document_draft) : le commit
    // (Enregistrer, Valider, Publier selon l'hôte), l'abandon (le destroy du brouillon, que
    // Quitter appelle), et l'avertissement de Quitter quand le document a changé.
    commitUrl: String,
    discardUrl: String,
    unsavedMessage: String,
  };

  // Autosave (éditeur pleine page monté sur un porteur persisté — visuel ou version de
  // template) : debounce + pastille, le bouton Enregistrer n'existe plus dans ce mode.
  connect() {
    // Le canvas ne représente pas encore la page courante (fabric charge en asynchrone) :
    // tant que c'est vrai, syncCurrent se tait — un flush qui tomberait là écraserait la
    // page avec un canvas vide ou en transit. Levé au premier signe de vie du canvas
    // (mutation, fin de bascule).
    this._canvasStale = true;
    if (this.hasDocumentUrlValue && this.documentUrlValue) {
      this._autosave = new Autosave({
        url: this.documentUrlValue,
        getPayload: () => {
          this.syncCurrent();
          return JSON.stringify(this.doc);
        },
        onState: (state) => this._setSaveState(state),
        onSaved: () => { if (this.hasFabricOutlet) this.fabricOutlet.reloadOverlayPanel(); },
      });
      this._autosave.prime();
      // La référence de « modifié ? » pour Quitter : le document tel qu'ouvert.
      this._openedPayload = JSON.stringify(this.doc);
    }
    // Un document né sans page (visuel vierge, body neuf) s'ouvre sur une page vierge, son
    // fond blanc compris, que la vue a servie au canvas comme au document — sans page
    // courante, syncCurrent n'aurait nulle part où rapatrier le canvas. Elle compte comme à
    // sauver : la base de l'autosave ne la connaît pas, elle se recale au premier flush
    // (jamais publiée, ne promeut pas).
    if (this.unsavedPageValue && this._autosave) {
      const page = this.slides.pop();
      this._autosave.prime();
      this.slides.push(page);
      this._autosave.schedule();
    }
    this._reflectCurrent();
    if (this._autosave) {
      this._draft = new DocumentDraft({
        element: this.element,
        commitUrl: this.commitUrlValue,
        discardUrl: this.discardUrlValue,
        autosave: this._autosave,
        isDirty: () => this._documentChanged(),
        confirmMessage: this.unsavedMessageValue,
      });
    }
  }

  // ==================== Brouillon : commit / abandon (lib/document_draft) ====================

  async commit(event) {
    const button = event?.currentTarget;
    button?.setAttribute("disabled", "");
    if (await this._draft?.commit()) return;

    button?.removeAttribute("disabled");
    this._setSaveState("error");
  }

  // Quitter (documents/_draft_actions, à côté du commit).
  onQuit() {
    this._draft?.quit();
  }

  // Télécharger (documents/_draft_actions) : l'url de l'export et le message d'échec sont les
  // params du bouton.
  async download({ currentTarget, params }) {
    currentTarget.disabled = true;
    await this._draft?.download(params.url, params.failure);
    currentTarget.disabled = false;
  }

  _documentChanged() {
    if (this._openedPayload === undefined) return false;
    this.syncCurrent();
    return JSON.stringify(this.doc) !== this._openedPayload;
  }

  // La pastille (compositions/_save_state) porte ses libellés et son mouvement : l'état suffit.
  _setSaveState(state) {
    if (this.hasSaveStateTarget) this.saveStateTarget.dataset.state = state;
  }

  /** Mutation du canvas fabric (bubbling depuis le sous-controller). */
  onCanvasChanged() {
    if (this.islandMode) return;
    // Une mutation vient d'avoir lieu SUR le canvas : il représente forcément la page
    // courante, les syncs redeviennent sûrs.
    this._canvasStale = false;
    this._autosave?.schedule();
  }

  compositionEditorSlidesOutletConnected() {
    this.markCurrent();
  }

  // Le canvas apprend quelle page il montre — la page vierge d'un document né sans page
  // n'existe que d'ici.
  fabricOutletConnected(outlet) {
    const page = this.slides[this.current];
    if (page) outlet.setPage(page.id, this.current + 1, this.legacyHtmlPage(page));
  }

  // Le frame de la bande vient de (re)rendre — au chargement comme après chaque morph
  // broadcast-driven : le rendu serveur ne connaît pas la page courante, on repose le ring.
  onStripLoaded(event) {
    if (!this.hasStripFrameTarget || event.target !== this.stripFrameTarget) return;

    this.markCurrent();
  }

  disconnect() {
    this._autosave?.destroy();
    this._draft?.destroy();
  }

  // Copie interne paresseuse : les Stimulus values re-parsent le DOM à chaque lecture, on ne
  // peut pas les muter — la vérité vit ici jusqu'à la sauvegarde. Paresseuse parce que les
  // callbacks d'outlets peuvent tirer avant connect().
  get doc() {
    this._doc ??= structuredClone(this.sourceDataValue);
    return this._doc;
  }

  // Îlot : le morph du frame réécrit la value serveur — la copie locale est périmée, elle se
  // re-clonera à la prochaine lecture.
  sourceDataValueChanged() {
    this._doc = null;
  }

  // Îlot du publisher (mode morph-driven) : la vérité est le serveur — le doc local ne sert
  // qu'à formuler les PATCHs, l'affichage vient du frame (refresh="morph").
  get islandMode() {
    return this.hasUpdateUrlValue && !!this.updateUrlValue;
  }

  get current() {
    this._current ??= this.initialCurrentValue || 0;
    return this._current;
  }

  set current(value) {
    this._current = value;
  }

  get slides() {
    return this.doc.slides;
  }

  // ==================== Mutations du document ====================

  // La vignette cliquée : sa page s'affiche et se sélectionne — la barre de son fond, comme
  // une séquence en vidéo. Un clic ailleurs la désélectionne sans changer de page.
  async selectSlide(index) {
    // Îlot : la sélection ouvre l'éditeur dans le panel, centré sur la slide.
    if (this.hasDraftFormTarget) return this.openEditor(index);
    if (index !== this.current) {
      this.syncCurrent();
      this.current = index;
      await this.showCurrent();
      this.markCurrent();
      this._reflectCurrent();
      // syncCurrent a pu rapatrier des layers : le flush (gratuit si rien n'a changé —
      // dirty-check) pousse la page qu'on quitte, le broadcast rafraîchit sa vignette.
      this._autosave?.flush();
    }
    if (this.hasFabricOutlet) this.fabricOutlet.showPageBar();
  }

  // Le faux slide « + » : une PAGE VIERGE — un design à écrire, les médias arrivent ensuite
  // (toolbox, drop, médias dynamiques). Depuis l'îlot : la page est persistée puis le panel
  // s'ouvre dessus ; dans l'éditeur : on s'y place.
  async addSlide() {
    this.syncCurrent();
    // Une seule page vierge à la fois : le « + » RÉUTILISE celle qui existe (jamais
    // d'empilement de pages vides — et elle n'est de toute façon jamais publiée).
    let index = this.slides.findIndex(isBlankSlide);
    if (index === -1) {
      this.slides.push(blankSlide(this.doc.width || 1080, this.doc.height || 1080));
      index = this.slides.length - 1;
      if (this.islandMode) await this.persist();
    }

    if (this.islandMode) {
      this.openEditor(index);
      return;
    }

    this.current = index;
    await this.afterMutation();
  }

  async duplicateSlide(index) {
    this.syncCurrent();
    const copy = structuredClone(this.slides[index]);
    copy.id = `slide-${crypto.randomUUID()}`;
    this.slides.splice(index + 1, 0, copy);
    if (this.hasFabricOutlet) this.current = index + 1;
    else if (index < this.current) this.current += 1;
    await this.afterMutation();
  }

  async removeSlide(index) {
    // L'éditeur garde au moins une page. Dans l'îlot, retirer la dernière page, c'est retirer
    // le visuel : le serveur remet la compo à vierge (confirmation Turbo sur le formulaire).
    if (this.slides.length <= 1) {
      if (this.hasClearFormTarget) this.clearFormTarget.requestSubmit();
      return;
    }

    this.slides.splice(index, 1);
    if (index < this.current) this.current -= 1;
    this.current = Math.min(this.current, this.slides.length - 1);
    await this.afterMutation();
  }

  async reorderSlides(oldIndex, newIndex) {
    if (oldIndex === newIndex) return;

    const [moved] = this.slides.splice(oldIndex, 1);
    this.slides.splice(newIndex, 0, moved);

    // La page courante suit son contenu.
    if (this.current === oldIndex) this.current = newIndex;
    else if (oldIndex < this.current && newIndex >= this.current) this.current -= 1;
    else if (oldIndex > this.current && newIndex <= this.current) this.current += 1;

    await this.afterMutation();
  }

  // Dans les deux modes la bande est serveur et broadcast-driven : on pousse le document
  // (PATCH), le broadcast-signal du Bearer (reload_frame) fait recharger le frame en morph
  // natif (refresh="morph"). Panel, îlot, autre onglet, collègue : même canal. Le flush est
  // immédiat — une mutation structurelle doit se voir sans attendre la debounce.
  async afterMutation() {
    if (this.islandMode) {
      await this.persist();
    } else {
      await this.showCurrent();
      this._reflectCurrent();
      await this._autosave?.flush();
    }
  }

  // La position courante, reflétée hors du doc : le champ slide de la fenêtre d'import
  // (l'état « page en cours » est purement client) et, en pleine page seulement, l'URL
  // (?slide=<id>) — F5, deep-links et re-renders serveur retombent sur la bonne page. Dans
  // un panel (frame), l'URL top appartient à la page hôte : on n'y touche pas.
  _reflectCurrent() {
    const slideId = this.slides[this.current]?.id || "";
    this.importFieldTargets.forEach((el) => { el.value = slideId; });

    if (!slideId || this.element.closest("turbo-frame")) return;
    const url = new URL(window.location.href);
    url.searchParams.set("slide", slideId);
    history.replaceState(history.state, "", url);
  }

  // Le champ slide arrive avec le contenu de la fenêtre d'import (frame chargé au clic).
  importFieldTargetConnected(el) {
    el.value = this.slides[this.current]?.id || "";
  }

  // L'import serveur (Documents::CompositionImportsController, StickerImportsController)
  // diffuse le document greffé :
  // on l'adopte tel quel — la base est déjà à jour, l'autosave se re-prime dessus. Les
  // îlots du publisher ne sont pas concernés (leur vérité est le morph serveur).
  async adoptImported({ detail }) {
    if (!detail?.document || this.islandMode || !this._autosave) return;

    this._doc = detail.document;
    this.current = Math.min(this.current, this.slides.length - 1);
    await this.showCurrent();
    this._reflectCurrent();
    this._autosave.prime();
    this._setSaveState("saved");
  }

  // ==================== Garder une sélection en calque ====================

  // La barre d'actions a dispatché les ids de la sélection (fabric#fcSaveAsSticker) : on
  // flush l'autosave — le serveur lit le document sauvegardé —, on poste, et la réponse
  // (un stream : flash, vignette prépendue dans l'onglet Calques s'il est ouvert) se rend.
  async saveSelectionAsSticker(event) {
    if (!this.hasStickerUrlValue || !this.stickerUrlValue) return;
    const { layerIds, pageId } = event.detail || {};
    if (!layerIds?.length) return;

    await this._autosave?.flush();
    const body = new URLSearchParams({ page: pageId || "" });
    layerIds.forEach((id) => body.append("layer_ids[]", id));
    const response = await fetch(this.stickerUrlValue, {
      method: "POST",
      headers: { Accept: "text/vnd.turbo-stream.html",
                 "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
      body,
    });
    Turbo.renderStreamMessage(await response.text());
  }

  // ==================== Mode persisté (îlot) ====================

  // La compo existe en base : chaque mutation la PATCHe directement.
  async persist() {
    if (!this.hasUpdateUrlValue || !this.updateUrlValue) return;

    await fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
      },
      body: JSON.stringify(this.doc),
    });
  }

  // Ouvre l'éditeur dans le panel du publisher, centré sur la slide cliquée. L'éditeur
  // s'ouvre sur un BROUILLON de la compo de l'îlot (plan document-drafts) : le formulaire
  // POSTe le brouillon, le serveur redirige vers son éditeur dans le frame document_editor,
  // dont l'hôte s'ouvre (lib/frame_hosts). Son Valider recopie dans la compo, dont le
  // broadcast morphe l'îlot.
  openEditor(index) {
    this.slideFieldTarget.value = this.slides[index]?.id || "";
    this.draftFormTarget.requestSubmit();
  }

  // ==================== Synchronisation canvas ↔ document ====================

  // Rapatrie les layers du canvas dans la page courante. Une compo encore vide (body neuf
  // du publisher) n'a pas de page courante — rien à synchroniser. Un canvas stale (bascule
  // en cours, chargement initial) non plus : le doc local est déjà la vérité des deux pages,
  // exporter écraserait la page d'arrivée avec un canvas en transit. Un canvas dont l'éditeur
  // n'est pas encore monté (catalogues de formes en vol, reconnexion après morph) non plus.
  syncCurrent() {
    if (!this.hasFabricOutlet || !this.fabricOutlet.ready || this._canvasStale || !this.slides[this.current]) return;

    this.slides[this.current].layers = this.fabricOutlet.exportLayers();
  }

  async showCurrent() {
    this.refreshHtmlPreview();
    if (!this.hasFabricOutlet) return;

    this._canvasStale = true;
    const page = this.slides[this.current];
    // LEGACY_ERB : la page dit à l'éditeur qu'un gabarit HTML (famille ERB) lui sert de fond.
    await this.fabricOutlet.showSlide(page?.layers || [], page?.id || null, this.current + 1, this.legacyHtmlPage(page));
    this._canvasStale = false;
    this.announcePage();
  }

  // La toolbox suit la page : l'itération d'un média dynamique n'a de sens que sur une page
  // répétée, sa carte ne se montre que là (contrôleur toolbox, `data-requires-repeat`).
  announcePage() {
    const page = this.slides[this.current];
    window.dispatchEvent(new CustomEvent("page:changed", { detail: { id: page?.id || null, repeat: !!page?.repeat } }));
  }

  // LEGACY_ERB : une page à gabarit HTML (famille ERB) — la directive ou la provenance du
  // document, ou un fond HTML servi sous le canvas pour cette page (instance du Renderer).
  legacyHtmlPage(page) {
    if (!page) return false;
    return !!legacyHtmlOf(page) || this.htmlPreviewTargets.some((el) => el.dataset.slideId === page.id);
  }

  // Les fonds HTML pas encore rasterisés (familles ERB transitoires) : rendus serveur sous
  // le canvas, un par slide concernée — on montre celui de la page courante.
  htmlPreviewTargetConnected() {
    this.refreshHtmlPreview();
  }

  // Le format se décide en cours d'édition (sélecteur du header) : le document et
  // l'artboard changent, les calques restent en place — fabric retaille son espace design.
  // Le format est un geste explicite, pas une frappe : le document part tout de suite (pas
  // de debounce), la bande et ses tuiles suivent au retour de la sauvegarde.
  async applyFormat(event) {
    const { width, height } = event.detail || {};
    if (!width || !height) return;

    this.doc.width = width;
    this.doc.height = height;
    if (this.hasFabricOutlet) await this.fabricOutlet.resizeArtboard(width, height);
    await this._autosave?.flush();
  }

  refreshHtmlPreview() {
    if (!this.hasHtmlPreviewTarget) return;

    const currentId = this.slides[this.current]?.id;
    this.htmlPreviewTargets.forEach((el) => {
      el.classList.toggle("hidden", el.dataset.slideId !== currentId);
    });
  }

  // La sélection, reflétée sur la bande (ring) et la barre repeat — un état purement client,
  // rejoué après chaque morph du frame. L'îlot n'a pas de page courante.
  markCurrent() {
    if (this.islandMode) return;

    if (this.hasCompositionEditorSlidesOutlet) {
      this.compositionEditorSlidesOutlet.markCurrent(this.current);
    }
    this.refreshRepeatBar();
  }

  // ==================== Contexte template : la directive repeat de la page courante ====
  // (plan media-template-generators §5 — la barre n'existe que montée sur une Version)

  repeatToggleTargetConnected() {
    this.refreshRepeatBar();
  }

  refreshRepeatBar() {
    if (!this.hasRepeatToggleTarget) return;

    const repeat = this.slides[this.current]?.repeat;
    this.repeatToggleTarget.checked = !!repeat;
    this.repeatFieldsTarget.classList.toggle("hidden", !repeat);
    if (repeat) {
      const [min, max] = repeat.range || [];
      this.repeatMinTarget.value = min ?? "";
      this.repeatMaxTarget.value = max ?? "";
    }
  }

  toggleRepeat() {
    const slide = this.slides[this.current];
    if (!slide) return;

    if (slide.repeat) {
      delete slide.repeat;
    } else {
      slide.repeat = { over: "media", range: [1, 8] };
    }
    this.refreshRepeatBar();
    this.announcePage();
    this.persist();
    this._autosave?.schedule();
  }

  updateRepeat() {
    const slide = this.slides[this.current];
    if (!slide?.repeat) return;

    const min = parseInt(this.repeatMinTarget.value, 10);
    const max = parseInt(this.repeatMaxTarget.value, 10);
    slide.repeat.range = [Number.isNaN(min) ? 1 : min, Number.isNaN(max) ? 8 : max];
    this.persist();
    this._autosave?.schedule();
  }
}
